import React from "react";

/**
 * DeliveryFeeStatus — the ONE way every checkout screen renders the delivery
 * fee (and the fee-inclusive total) from useDeliveryFee().
 *
 * Contract (Playbook 12 — Order Placement Contract, §C3):
 *   • isReady   → render the formatted value.
 *   • loading   → "Calculating…" (transient, only while a quote is in flight).
 *   • isFailed  → visible error + a Retry action. NEVER a silent, permanent
 *                 "Calculating…" — that dead-ends checkout (July-2026 prod bug).
 *   • isBlocked → the quote can't even be requested (address has no coords):
 *                 tell the customer to re-select their delivery location.
 *
 * Keep this component dependency-free (plain spans, inline styles) — checkout
 * screens mix @material-ui v4 and @mui v5, so it must not bind to either.
 */
const styles = {
  error: { color: "#b71c1c", fontSize: 13, fontStyle: "italic" },
  blocked: { color: "#b26a00", fontSize: 13, fontStyle: "italic" },
  calculating: { color: "#607d8b", fontSize: 14, fontStyle: "italic" },
  retry: {
    marginLeft: 8,
    color: "#00668b",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
  },
};

const DeliveryFeeStatus = ({
  isReady,
  isFailed,
  isBlocked,
  error,
  onRetry,
  value,
  freightQuotedSeparately = false,
  blockedMessage = "We couldn't read your delivery location — please go back and re-select your address.",
  failedMessage = "We couldn't calculate the delivery fee.",
}) => {
  if (isReady) return <span>{freightQuotedSeparately ? "Quoted separately" : value}</span>;

  if (isFailed) {
    return (
      <span style={styles.error}>
        {typeof error === "string" && error.trim() ? error : failedMessage}
        {onRetry && (
          <button type="button" style={styles.retry} onClick={onRetry}>
            Retry
          </button>
        )}
      </span>
    );
  }

  if (isBlocked) {
    return <span style={styles.blocked}>{blockedMessage}</span>;
  }

  return <span style={styles.calculating}>Calculating…</span>;
};

export default DeliveryFeeStatus;
